import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Logo from './Logo';
import FooterBrands from './FooterBrands';
import { pagesAPI, api } from '../services/api';
import { Facebook, Instagram, CreditCard, Truck, MapPin, Phone, Mail, MessageCircle } from 'lucide-react';
import { useT } from '../services/i18n';
import { consent } from '../services/consent';
import { pageTitle, pageGroupLabel } from '../services/catalogI18n';
import { useOrganizationJsonLd } from '../services/organizationJsonLd';

const FALLBACK_LINKS = {
  'Assistance & Service': ['Aide & FAQ', 'Service client', 'Conditions de livraison', 'Modes de paiement', 'Politique de retour'],
  'Entreprise': ['À propos d\'OTOPIECES.COM', 'Carrières', 'Presse', 'Affiliations', 'Partenaires'],
  'Informations juridiques': ['Mentions légales', 'Conditions générales', 'Protection des données', 'Cookies', 'Droit de rétractation'],
};

const Footer = () => {
  const { t, lang } = useT();
  const [pagesData, setPagesData] = useState(null);
  const [company, setCompany] = useState({});
  useOrganizationJsonLd(company);

  useEffect(() => {
    pagesAPI.list().then(setPagesData).catch(() => setPagesData(null));
    api.get('/company').then(r => setCompany(r.data || {})).catch(() => {});
  }, []);
  const waNumber = (company.phone || '').replace(/\D/g, '');

  const footerLinks = pagesData && pagesData.pages.length
    ? Object.fromEntries(Object.entries(pagesData.groups).map(([key]) => [pageGroupLabel(pagesData.groups, pagesData.groups_i18n, key, lang), pagesData.pages.filter(p => p.group === key).map(p => ({ ...p, title: pageTitle(p, lang) }))]).filter(([, list]) => list.length))
    : Object.fromEntries(Object.entries(FALLBACK_LINKS).map(([label, names]) => [label, names.map(n => ({ title: n, slug: null }))]));

  const paymentMethods = [
    'Visa', 'Mastercard', 'PayPal', 'Apple Pay', 'Google Pay'
  ];

  return (
    <footer className="bg-[#0e0e10] text-white">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-8">
          {/* Logo and app download */}
          <div className="lg:col-span-2">
            <div className="mb-5">
              <Logo variant="dark" size="ml" />
            </div>

            {/* Contact */}
            <div className="mb-6 space-y-2 text-sm" data-testid="footer-contact">
              <p className="text-sm font-medium mb-3">{t('footer.contactUs')}</p>
              {company.address && <p className="flex items-start gap-2 text-gray-400" data-testid="footer-address"><MapPin size={16} className="mt-0.5 text-orange-500 flex-shrink-0" /> <span>{company.address}<br />{company.postal_code} {company.city}</span></p>}
              {company.phone && <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-gray-400 hover:text-orange-500" data-testid="footer-phone"><Phone size={16} className="text-orange-500" /> {company.phone} <span className="inline-flex items-center gap-1 text-[10px] bg-green-600 text-white px-1.5 py-0.5 rounded-full"><MessageCircle size={10} /> WhatsApp</span></a>}
              {company.email && <a href={`mailto:${company.email}`} className="flex items-center gap-2 text-gray-400 hover:text-orange-500" data-testid="footer-email"><Mail size={16} className="text-orange-500" /> {company.email}</a>}
              {company.opening_hours && <p className="text-xs text-gray-500 pl-6">{company.opening_hours}</p>}
            </div>

            {/* Social links */}
            {(company.facebook_url || company.instagram_url || company.tiktok_url) && (
              <div className="flex gap-3" data-testid="footer-social">
                {company.facebook_url && <a href={company.facebook_url} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="footer-social-facebook" className="w-10 h-10 bg-gray-800 hover:bg-orange-500 rounded-full flex items-center justify-center transition-colors"><Facebook size={18} /></a>}
                {company.instagram_url && <a href={company.instagram_url} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="footer-social-instagram" className="w-10 h-10 bg-gray-800 hover:bg-orange-500 rounded-full flex items-center justify-center transition-colors"><Instagram size={18} /></a>}
                {company.tiktok_url && <a href={company.tiktok_url} target="_blank" rel="noreferrer" aria-label="TikTok" data-testid="footer-social-tiktok" className="w-10 h-10 bg-gray-800 hover:bg-orange-500 rounded-full flex items-center justify-center transition-colors"><img src={`${process.env.REACT_APP_BACKEND_URL}/api/branding/social/tiktok.png`} alt="TikTok" className="w-5 h-5 rounded" /></a>}
              </div>
            )}
          </div>

          {/* Footer links */}
          <div data-testid="footer-group-otopiece">
            <h3 className="font-semibold mb-4 text-sm">OTOPIECES.COM</h3>
            <ul className="space-y-2">
              {[['/contact', t('footer.contact')], ['/livraison', t('footer.shipping')], ['/garantie-retours', t('footer.returns')], ['/qui-sommes-nous', t('footer.about')], ['/budget-entretien', t('footer.budget')], ['/plan-du-site', t('footer.sitemap')]].map(([to, label]) => (
                <li key={to}><Link to={to} data-testid={`footer-link-${to.slice(1)}`} className="text-sm text-gray-400 hover:text-orange-500 transition-colors">{label}</Link></li>
              ))}
              <li><button type="button" onClick={consent.open} data-testid="footer-manage-cookies" className="text-sm text-gray-400 hover:text-orange-500 transition-colors">{t('cookies.manage')}</button></li>
            </ul>
          </div>
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title} data-testid={`footer-group-${title}`}>
              <h3 className="font-semibold mb-4 text-sm">{title}</h3>
              <ul className="space-y-2">
                {links.map((link, idx) => (
                  <li key={link.slug || idx}>
                    {link.slug ? (
                      <Link to={`/page/${link.slug}`} data-testid={`footer-page-${link.slug}`} className="text-sm text-gray-400 hover:text-orange-500 transition-colors">
                        {link.title}
                      </Link>
                    ) : (
                      <span className="text-sm text-gray-400">{link.title}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <FooterBrands />

        {/* Payment and shipping */}
        <div className="mt-10 pt-8 border-t border-gray-800">
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2 flex-wrap">
              <CreditCard size={18} className="text-gray-400" />
              <span className="text-sm text-gray-400">{t('footer.securePayments')}</span>
              <div className="flex gap-2 flex-wrap">
                {paymentMethods.map((method, idx) => (
                  <span
                    key={idx}
                    className="text-xs bg-gray-800 px-2 py-1 rounded"
                  >
                    {method}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Truck size={18} className="text-gray-400" />
              <span className="text-sm text-gray-400">{t('footer.fastShipping')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-gray-500">
              {t('footer.rights')}
            </p>
            <p className="text-xs text-gray-500">
              {t('footer.trademarks')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
